import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { MaskitoElementPredicate, MaskitoOptions } from '@maskito/core';
import { select, Store } from '@ngrx/store';

import { isEqual, isNull } from 'lodash-es';
import { BehaviorSubject, Observable, throttleTime } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { FORM_SECTION } from '../../../../../constants/form.constant';
import { amountMask, rateAmountMask } from '../../../../../constants/maskito.constants';
import { NetSheetTermEnum } from '../../../../../enums/net-sheet';
import { showComponentByFieldsVisibility } from '../../../../../helpers/form';
import * as fromNetSheetActions from '../../../../../store/actions/net-sheet.actions';
import * as fromNetSheetSelectors from '../../../../../store/selectors/net-sheet.selectors';
import { FormSection } from '../../../../../typings/form';
import {
  NetSheetFormFieldSettings,
  NetSheetTaxesHomeownersDuesTerm,
  NetSheetTaxesTerm
} from '../../../../../typings/net-sheet';
import { GenericComponent } from '../../../../components/generic/generic.component';
import { DatePickerService } from '../../../../services/date-picker.service';

interface FormModel {
  taxes: FormControl<string | null>;
  taxesHomeownersDuesAmount: FormControl<string | null>;
  taxesHomeownersDuesDueDate: FormControl<string | null>;
  taxesHomeownersDuesPaid: FormControl<boolean>;
  taxesHomeownersDuesTerm: FormControl<NetSheetTaxesHomeownersDuesTerm | null>;
  taxesNextInstallment: FormControl<string | null>;
  taxesPaid: FormControl<boolean>;
  taxesPaidToDateDropdown: FormControl<string | null>;
  taxesPaidToDatePicker: FormControl<string | null>;
  taxesTerm: FormControl<NetSheetTaxesTerm | null>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: FORM_SECTION,
      useExisting: TaxesComponent
    }
  ],
  selector: 'red-net-shet-taxes',
  standalone: false,
  templateUrl: './taxes.component.html'
})
export class TaxesComponent extends GenericComponent implements OnInit, FormSection {
  private store = inject(Store);
  private datePickerService = inject(DatePickerService);

  showComponent$ = new BehaviorSubject(true);

  form = new FormGroup<FormModel>({
    taxes: new FormControl<string | null>(null),
    taxesHomeownersDuesAmount: new FormControl<string | null>(null),
    taxesHomeownersDuesDueDate: new FormControl<string | null>(null),
    taxesHomeownersDuesPaid: new FormControl<boolean>(false, {
      nonNullable: true
    }),
    taxesHomeownersDuesTerm: new FormControl<NetSheetTaxesHomeownersDuesTerm | null>(null),
    taxesNextInstallment: new FormControl<string | null>(null),
    taxesPaid: new FormControl<boolean>(false, {
      nonNullable: true
    }),
    taxesPaidToDateDropdown: new FormControl<string | null>(null),
    taxesPaidToDatePicker: new FormControl<string | null>(null),
    taxesTerm: new FormControl<NetSheetTaxesTerm | null>(null)
  });

  taxesPaidToDateDropdownData$: Observable<string[]>;
  availableTaxesTerms = [NetSheetTermEnum.Quarterly, NetSheetTermEnum.SemiAnnually, NetSheetTermEnum.Annually];
  availableHomeownersDuesTerms = [
    NetSheetTermEnum.Monthly,
    NetSheetTermEnum.Quarterly,
    NetSheetTermEnum.SemiAnnually,
    NetSheetTermEnum.Annually
  ];

  fieldsSettings$ = new BehaviorSubject<{ [key: string]: NetSheetFormFieldSettings } | null>(null);

  constructor() {
    super();

    this.form.valueChanges.subscribe((values) => {
      this.store.dispatch(
        fromNetSheetActions.updateTaxes({
          payload: {
            ...values
          }
        })
      );
      this.valid.set(this.form.valid);
    });

    this.taxesPaidToDateDropdownData$ = this.store.pipe(
      select(fromNetSheetSelectors.selectTaxesPaidToDateDropdownData),
      throttleTime(200, undefined, { leading: true, trailing: true }),
      distinctUntilChanged((previous, current) => isEqual(previous, current))
    );
  }

  ngOnInit() {
    this.addUniqueSubscription(
      'fieldsDataSubscription',
      this.store
        .pipe(
          select(fromNetSheetSelectors.selectTaxesFieldsData),
          throttleTime(200, undefined, { leading: true, trailing: true }),
          distinctUntilChanged((previous, current) => isEqual(previous, current))
        )
        .subscribe((data) => {
          this.form.patchValue(data, { emitEvent: false });
          this.valid.set(this.form.valid);
        })
    );

    this.addUniqueSubscription(
      'fieldsSettingsSubscription',
      this.store
        .pipe(
          select(fromNetSheetSelectors.selectTaxesFieldsSettings),
          throttleTime(200, undefined, { leading: true, trailing: true }),
          distinctUntilChanged((previous, current) => isEqual(previous, current))
        )
        .subscribe((fields) => {
          this.showComponent$.next(showComponentByFieldsVisibility(fields));
          this.fieldsSettings$.next(fields);
          setTimeout(() => {
            this.valid.set(this.form.valid);
          });
        })
    );
  }

  async onDatePickerInputClick(event: MouseEvent, fieldName: string) {
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();

    const control = this.form.get(fieldName);

    if (!isNull(control)) {
      const date = await this.datePickerService.open(control.value ?? null);

      if (!isNull(date)) {
        this.form.patchValue({
          [fieldName]: date
        });
      }
    }
  }

  readonly amountMask: MaskitoOptions = amountMask;
  readonly rateAmountMask: MaskitoOptions = rateAmountMask(20);
  readonly maskPredicate: MaskitoElementPredicate = async (el) =>
    (el as unknown as HTMLIonInputElement).getInputElement();
  readonly valid = signal(this.form.valid);
}
